// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Card component module for visualizing customer asset ownership data.
 * Displays a stacked bar chart showing owned vs sold assets per customer.
 */
import { BarChartCard } from '@/components/bar-chart-card';
import { CustomerOwnershipReport } from '@/customer/api/get-customer-report';
import {
  CustomerReportSort,
  useCustomerReport,
} from '@/customer/hooks/use-customer-report';
import { colors } from '@mui/material';

/**
 * Color constants for the ownership status visualization.
 * Uses teal for owned assets and tan for sold assets.
 */
const SOLD_COLOR = '#d2b48c'; // Tan color for sold assets
const OWNED_COLOR = colors.teal[500]; // Teal color for owned assets

/**
 * Type definition for the keys we want to extract from CustomerOwnershipReport.
 * Specifically targets the count fields for owned and sold assets.
 */
type Key = Extract<keyof CustomerOwnershipReport, 'ownedCount' | 'soldCount'>;

/**
 * Array of keys to use for the bar chart data series.
 * Order determines the stacking order in the visualization.
 * Shows owned assets first, followed by sold assets.
 */
const KEYS: Key[] = ['ownedCount', 'soldCount'];

/**
 * Extracts the customer name from a report datum for labeling.
 * Used to label each bar in the chart with the corresponding customer name.
 *
 * @param datum - The customer ownership report entry
 * @returns The customer's name as a string
 */
function getLabelForDatum(datum: CustomerOwnershipReport) {
  return datum.customer.name;
}

/**
 * Maps data keys to their display labels.
 * Converts technical keys to user-friendly display text.
 *
 * @param key - The data key to get a label for
 * @returns User-friendly label for the given key
 */
function getLabelForKey(key: Key) {
  switch (key) {
    case 'ownedCount':
      return 'Owned';

    case 'soldCount':
      return 'Sold';
  }
}

/**
 * Props for the CustomerOwnershipReportCard component
 */
interface Props {
  /**
   * Sort configuration for the customer report data.
   * Determines the order of customers in the visualization.
   */
  sort: CustomerReportSort;

  /**
   * Optional maximum height for the chart container.
   * Accepts CSS values like '40rem' or 500.
   */
  maximumHeight?: number | string;
}

/**
 * A card component that displays a stacked bar chart of customer asset ownership status.
 *
 * Features:
 * - Stacked bar visualization of owned vs sold assets
 * - Color-coded bars (teal for owned, tan for sold)
 * - Automatic data loading and sorting
 * - Loading state handling
 * - Empty state messaging
 * - Responsive design
 *
 * Visual Elements:
 * - Title showing "Asset ownership"
 * - Stacked bars for each customer
 * - Color-coded segments for ownership status
 * - Customer names as labels
 * - Loading/empty states when applicable
 *
 * @example
 * ```tsx
 * <CustomerOwnershipReportCard
 *   sort={{ property: 'count', direction: 'desc' }}
 * />
 * ```
 */
export function CustomerOwnershipReportCard(props: Props) {
  // Fetch and sort ownership data
  const { ownershipData, isLoading } = useCustomerReport(props.sort);

  // Define colors for ownership status (teal=owned, tan=sold)
  const colors = [OWNED_COLOR, SOLD_COLOR];

  // Determine empty state message based on loading status
  const emptyState = isLoading
    ? 'Loading'
    : 'There is no customer data to display';

  return (
    <BarChartCard
      title="Asset ownership"
      emptyState={emptyState}
      keys={KEYS}
      data={ownershipData}
      colors={colors}
      getLabelForKey={getLabelForKey}
      getLabelForDatum={getLabelForDatum}
      pluralizeTooltipOptions={{ singular: 'asset' }}
      maximumHeight={props.maximumHeight}
    />
  );
}
